import { graphql } from '@/gql'

export const CreateAccessTokenMutation = graphql(`
  mutation CreateAccessToken($username: String!, $password: String!) {
    createAccessToken(username: $username, password: $password) {
      token
      parseToken
      expires
      user {
        id
        name
        username
      }
    }
  }
`)

export const MyRestaurantsQuery = graphql(`
  query MyRestaurants {
    me {
      id
      name
      username
      restaurantsWithAccess(access: Read) {
        id
        name
        city
        galleyEnabled: attribute(key: "galley.enabled")
      }
    }
  }
`)

export const RestaurantKitchenDisplaysQuery = graphql(`
  query RestaurantKitchenDisplays($restaurantId: ID!) {
    restaurant(id: $restaurantId) {
      id
      name
      kitchenDisplaysDisabled
      kitchenDisplays {
        id
        name
        description
      }
    }
  }
`)

export const KitchenDisplayQuery = graphql(`
  query KitchenDisplay($restaurantId: ID!, $kitchenDisplayId: ID!) {
    restaurant(id: $restaurantId) {
      id
      name
      kitchenDisplaysDisabled
      galleyEnabled: attribute(key: "galley.enabled")
      printerQueues { id name physicalPrinters { id name isOnline } }
      kitchenDisplay(id: $kitchenDisplayId) {
        id
        name
        description
      }
    }
  }
`)

/**
 * Everything one screen shows. A slip is one order as far as this display is concerned;
 * its sections are the parts of it, and a section belonging to another station comes
 * through marked so the screen can tell them apart.
 */

export const KitchenDisplayDataQuery = graphql(`
  query KitchenDisplayData($restaurantId: ID!, $kitchenDisplayId: ID!) {
    restaurant(id: $restaurantId) {
      id
      name
      queueTime
      kitchenDisplayPreorderThreshold
      kitchenDisplaySlipDoneWarnOnIncompleteItems
      kitchenDisplayDeliveryBackgroundColor
      kitchenDisplayEatInBackgroundColor
      kitchenDisplayTakeAwayBackgroundColor
      kitchenDisplays {
        id
        name
      }
      printerQueues { id name }
      kitchenDisplay(id: $kitchenDisplayId) {
        id
        name
        showsPrintButton
        skipExplicitSlipPreparationStart
        slipSortOrder
        transactionChannelFilter
        lateColorRules {
          threshold
          color
        }
        printerQueue {
          id
          name
        }
        slips {
          id
          humanOrderCode
          kitchenStationProgress {
            kitchenDisplayId
            state
          }
          createdAt
          deliveryTime
          estimatedDeliveryTime
          riderPickupTime
          orderType
          backgroundColor
          message
          needsConfirmation
          readyForPickup
          transactionChannel
          tableName
          till {
            id
            name
          }
          lineItemSections {
            id
            title
            isSecondary
            isCollapsed
            items {
              id
              name
              count
              message
              productId
              holdKitchenPrint
              needsKitchenPrint
              preparationStartedAt
              preparationCompletedAt
              preparationBy {
                id
                name
              }
              alternativeItems {
                id
                prefix
                name
                alternativeGroupId
                alternativeItemName
                count
                isChosen
                isDefault
                kitchenPresentation
                productId
              }
            }
          }
        }
      }
    }
  }
`)

/**
 * The main screen asks every station rather than only itself. Asking for one display
 * returns only what that display is set up to see, so a main screen would show half an
 * order and the kitchen would send it out incomplete.
 */
export const AggregatedDisplayDataQuery = graphql(`
  query AggregatedDisplayData($restaurantId: ID!) {
    restaurant(id: $restaurantId) {
      id
      name
      queueTime
      kitchenDisplayPreorderThreshold
      kitchenDisplaySlipDoneWarnOnIncompleteItems
      kitchenDisplayDeliveryBackgroundColor
      kitchenDisplayEatInBackgroundColor
      kitchenDisplayTakeAwayBackgroundColor
      kitchenDisplays {
        id
        name
        showsPrintButton
        skipExplicitSlipPreparationStart
        slipSortOrder
        transactionChannelFilter
        lateColorRules {
          threshold
          color
        }
        printerQueue {
          id
          name
        }
        slips {
          id
          humanOrderCode
          kitchenStationProgress {
            kitchenDisplayId
            state
          }
          createdAt
          deliveryTime
          estimatedDeliveryTime
          riderPickupTime
          orderType
          backgroundColor
          message
          needsConfirmation
          readyForPickup
          transactionChannel
          tableName
          till {
            id
            name
          }
          lineItemSections {
            id
            title
            isSecondary
            isCollapsed
            items {
              id
              name
              count
              message
              productId
              holdKitchenPrint
              needsKitchenPrint
              preparationStartedAt
              preparationCompletedAt
              preparationBy {
                id
                name
              }
              alternativeItems {
                id
                prefix
                name
                alternativeGroupId
                alternativeItemName
                count
                isChosen
                isDefault
                kitchenPresentation
                productId
              }
            }
          }
        }
      }
    }
  }
`)

/**
 * What the kitchen does to an order. Each one names a single line, a whole slip, or the
 * order behind it, because a screen is touched with a wet glove and wants one press per
 * decision rather than a menu.
 *
 * They all return the whole slip rather than the line, so one press refreshes everything
 * that press changed — including whether the slip as a whole is now ready.
 */
export const StartPreparingLineItemMutation = graphql(`
  mutation StartPreparingLineItem($restaurantId: ID!, $digitalSlipId: ID!, $lineItemId: ID!) {
    startPreparingDigitalSlipLineItem(
      restaurantId: $restaurantId
      digitalSlipId: $digitalSlipId
      digitalSlipLineItemId: $lineItemId
    ) {
      id
      readyForPickup
      lineItemSections {
        id
        items {
          id
          preparationStartedAt
          preparationCompletedAt
          preparationBy {
            id
            name
          }
        }
      }
    }
  }
`)

export const FinishPreparingLineItemMutation = graphql(`
  mutation FinishPreparingLineItem($restaurantId: ID!, $digitalSlipId: ID!, $lineItemId: ID!) {
    finishPreparingDigitalSlipLineItem(
      restaurantId: $restaurantId
      digitalSlipId: $digitalSlipId
      digitalSlipLineItemId: $lineItemId
    ) {
      id
      readyForPickup
      lineItemSections {
        id
        items {
          id
          preparationStartedAt
          preparationCompletedAt
          preparationBy {
            id
            name
          }
        }
      }
    }
  }
`)

/** Takes a finished line back to being in progress, for the press that was a mistake. */
export const UnfinishPreparingLineItemMutation = graphql(`
  mutation UnfinishPreparingLineItem($restaurantId: ID!, $digitalSlipId: ID!, $lineItemId: ID!) {
    unfinishPreparingDigitalSlipLineItem(
      restaurantId: $restaurantId
      digitalSlipId: $digitalSlipId
      digitalSlipLineItemId: $lineItemId
    ) {
      id
      readyForPickup
      lineItemSections {
        id
        items {
          id
          preparationStartedAt
          preparationCompletedAt
        }
      }
    }
  }
`)

/** Takes a line all the way back to untouched. */
export const ClearPreparingLineItemMutation = graphql(`
  mutation ClearPreparingLineItem($restaurantId: ID!, $digitalSlipId: ID!, $lineItemId: ID!) {
    clearPreparingDigitalSlipLineItem(
      restaurantId: $restaurantId
      digitalSlipId: $digitalSlipId
      digitalSlipLineItemId: $lineItemId
    ) {
      id
      readyForPickup
      lineItemSections {
        id
        items {
          id
          preparationStartedAt
          preparationCompletedAt
        }
      }
    }
  }
`)

export const FinishPreparingSlipMutation = graphql(`
  mutation FinishPreparingSlip($restaurantId: ID!, $digitalSlipId: ID!) {
    finishPreparingDigitalSlip(restaurantId: $restaurantId, digitalSlipId: $digitalSlipId) {
      id
      readyForPickup
    }
  }
`)

/** Marks a Main-screen ticket ready for pickup after the API's print checks pass. */
export const FinishOrdersMutation = graphql(`
  mutation FinishOrders($restaurantId: ID!, $digitalSlipId: ID!, $input: FinishOrdersInput) {
    finishOrders(restaurantId: $restaurantId, digitalSlipId: $digitalSlipId, input: $input) {
      id
      readyForPickup
    }
  }
`)

export const ClearSlipPreparationMutation = graphql(`
  mutation ClearSlipPreparation($restaurantId: ID!, $digitalSlipId: ID!) {
    clearDigitalSlipPreparationStatus(restaurantId: $restaurantId, digitalSlipId: $digitalSlipId) {
      id
      readyForPickup
    }
  }
`)

export const PrintDigitalSlipMutation = graphql(`
  mutation PrintDigitalSlip($restaurantId: ID!, $digitalSlipId: ID!, $printerQueueId: ID!) {
    printDigitalSlip(
      restaurantId: $restaurantId
      digitalSlipId: $digitalSlipId
      input: { printerQueueId: $printerQueueId }
    )
  }
`)

/**
 * What the backoffice set for this screen. There may be no record at all — a screen
 * nobody has configured runs on the defaults — so every field is read defensively.
 */
/** Takes a collected order off the rail, and off the screen the guest is watching. */
export const MarkOrderAsPickedUpMutation = graphql(`
  mutation MarkOrderAsPickedUp($restaurantId: ID!, $digitalSlipId: ID!) {
    markOrderAsPickedUp(restaurantId: $restaurantId, digitalSlipId: $digitalSlipId) {
      id
      readyForPickup
    }
  }
`)

/**
 * What the guests' screen shows. Orders come from every kitchen display, because the
 * screen in the dining room is about the order as a whole rather than any one station.
 */
export const OrderDisplayDataQuery = graphql(`
  query OrderDisplayData($restaurantId: ID!) {
    restaurant(id: $restaurantId) {
      id
      name
      kitchenDisplaysDisabled
      galleyEnabled: attribute(key: "galley.enabled")
      kitchenDisplays {
        id
        slips {
          id
          humanOrderCode
          createdAt
          orderType
          readyForPickup
          transactionChannel
        }
      }

    }
  }
`)

/** How many screens this kitchen has, which decides what an unconfigured screen shows. */
export const KitchenDisplayCountQuery = graphql(`
  query KitchenDisplayCount($restaurantId: ID!) {
    restaurant(id: $restaurantId) {
      id
      kitchenDisplays {
        id
      }
    }
  }
`)

/** Existing kiosk flow supplies component ownership; receipts supply actual selections. */
export const KitchenMenuQuery = graphql(`
  query KitchenMenu($restaurantId: ID!) {
    restaurant(id: $restaurantId) {
      id
      menu(filter: { active: All, includeProductsWithOpenPrice: true }) {
        pages {
          categories {
            products {
              id
              kioskFlow: attribute(key: "galley.kioskFlow")
              eatIn: properties(menuType: EatIn) {
                id
              }
              takeAway: properties(menuType: TakeAway) {
                id
              }
            }
          }
        }
      }
    }
  }
`)

export const ConfigurationQuery = graphql(`
  query Configuration($restaurantId: ID!) {
    restaurant(id: $restaurantId) {
      id
      galleyEnabled: attribute(key: "galley.enabled")
      kitchenDisplaysDisabled
      configuration: entities(class: "galley.configuration", first: 2) {
        edges { node { id updatedAt document: attribute(key: "galley.document") } }
      }
    }
  }
`)

export const AcceptOrderMutation = graphql(
  `mutation GalleyAcceptOrder($restaurantId: ID!, $digitalSlipId: ID!) { acceptOrder(restaurantId: $restaurantId, digitalSlipId: $digitalSlipId) { id needsConfirmation } }`,
)
export const DeclineOrderMutation = graphql(`mutation GalleyDeclineOrder($restaurantId: ID!, $digitalSlipId: ID!) { declineOrder(restaurantId: $restaurantId, digitalSlipId: $digitalSlipId) { id } }`)
